
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import DateTimePicker from '@/components/DateTimePicker.vue'
import { Transport } from '@/models/Transport'
import InvoiceLineForm from '@/components/Transport/Accounting/InvoiceLineForm.vue'
import InvoicePreview from '@/components/Transport/Accounting/InvoicePreview.vue'
import { Customer } from '@/models/accounting/Customer'
import { Product } from '@/models/accounting/Product'
import { VatCode } from '@/models/accounting/VatCode'
import { Journal } from '@/models/accounting/Journal'
import { Invoice } from '@/models/accounting/Invoice'
import { InvoiceLine } from '@/models/accounting/InvoiceLine'
import axios from 'axios'
import { PluralResponse } from 'coloquent/dist/response/PluralResponse'
import { namespace } from 'vuex-class'
import InvoiceStatus from '@/enums/InvoiceStatus'
import { formatCurrency } from '@/utils/currency'
import merge from 'deepmerge'
import InvoiceType from '@/enums/InvoiceType'
import Testable from '@/mixins/Testable'
import { FlatTransport } from '@/models/FlatTransport'
import FinancialType from '@/enums/FinancialType'
import FileTag from '@/enums/FileTag'
import InvoiceAttachments from '@/components/Transport/Accounting/InvoiceAttachments.vue'
import TransportStatus from '@/enums/TransportStatus'
import InvoiceLineStatus from '@/enums/InvoiceLineStatus'
import CustomerSelector from '@/components/Accounting/CustomerSelector.vue'
import { Company } from '@/models/Company'
import moment from 'moment'

const Auth = namespace('auth')

@Component({
	computed: {
		FileTag() {
			return FileTag
		},
		InvoiceStatus() {
			return InvoiceStatus
		},
		InvoiceType() {
			return InvoiceType
		}
	},
	methods: { formatCurrency, moment },
	components: {
		CustomerSelector,
		InvoiceAttachments,
		DateTimePicker,
		InvoiceLineForm,
		InvoicePreview
	}
})
export default class InvoiceCreator extends Mixins(Testable) {
	@Auth.Getter('hasInternalFinancialConnection')
	hasInternalFinancialConnection

	@Auth.Getter('hasExactFinancialConnection')
	hasExactFinancialConnection

	@Auth.Getter('firstDayOfWeek')
	firstDayOfWeek!: string

	@Auth.Getter('currentCompany')
	currentCompany!: Company

	@Prop({ required: false, default: () => [] })
	transports!: Array<FlatTransport> | Array<Transport>

	@Prop({ required: false, default: false })
	preview!: boolean

	@Prop({ required: false, default: false })
	sending!: boolean

	@Prop({ required: false, default: false })
	showingTruckTransports!: boolean

	@Prop({ required: false, default: null })
	invoice!: Invoice | null

	@Prop({ required: false, default: null })
	invoiceType!: InvoiceType | null

	@Prop({ required: false, default: false })
	isCombinedInvoice!: boolean

	@Prop({ required: false, default: false })
	isExternal!: boolean

	isLoading: boolean = false
	initialized: boolean = false
	initializing: boolean = true
	isEditing: boolean = false
	showAttachments: boolean = false
	invoiceDatepickerModal: boolean = false
	setDossierClosed: boolean = false

	filterByRelation: Customer | null = null
	companies: Array<Customer> = []

	customers: Array<Customer> = []
	products: Array<Product> = []
	journals: Array<Journal> = []
	internalInvoice: Invoice = new Invoice()

	invoiceLines: Array<InvoiceLine> = []

	invoiceTotal: number = 0

	// used to trigger a correct rerender of the invoice lines form because it has an index based key
	lineCounter: number = 0
	attachmentsCounter: number = 0

	selectedCustomer: Customer | null = null
	initialCreditor: Customer | null = null
	initialDebitor: Customer | null = null

	selectedJournal: Journal | null = null

	salesOrPurchase: InvoiceType.SALES | InvoiceType.PURCHASE = InvoiceType.PURCHASE

	@Watch('salesOrPurchase')
	async onSalesOrPurchaseChange() {
		if (!this.initialDebitor || !this.initialCreditor) return
		this.selectedCustomer = this.salesOrPurchase === InvoiceType.SALES ? this.initialDebitor : this.initialCreditor
	}

	formatRelationTypes(types: Array<string>) {
		return types?.map((type) => this.$t(`invoiceCreator.companyType.${type}`)).join(', ')
	}

	get haveSameClient() {
		if (this.transports.length === 0) return false
		const firstClientId = this.transports[0]?.clientId
		// @ts-ignore eslint-disable-next-line
		return this.transports.reduce((same, transport) => same && transport.clientId === firstClientId, true)
	}
	get haveSameVessel() {
		if (this.transports.length === 0) return false
		const firstVesselId = (this.transports[0] as FlatTransport)?.vesselId
		// @ts-ignore eslint-disable-next-line
		return this.transports.reduce(
			(same, transport) => same && (transport as FlatTransport).vesselId === firstVesselId,
			true
		)
	}

	get formattedTotalInclVAT() {
		// eslint-disable-next-line no-unused-expressions
		this.invoiceTotal // just referencing the property so that it stays in sync, work around array binding issue
		return (
			formatCurrency(
				this.filteredInvoiceLines.reduce((acc, line) => {
					return acc + line.totalInclVAT
				}, 0)
			) +
			' ' +
			this.$i18n.t('invoiceCreator.incl_vat')
		)
	}

	async created() {
		this.salesOrPurchase = this.invoiceType || InvoiceType.SALES

		if (this.isCombinedInvoice) {
			this.salesOrPurchase = this.haveSameClient && !this.isExternal ? InvoiceType.SALES : InvoiceType.PURCHASE

			const vessel = await (this.transports[0] as FlatTransport)?.fetchVesselLazy()
			let debitorQuery = Promise.resolve(null as Customer | null)
			let creditorQuery = Promise.resolve(null as Customer | null)
			if (this.transports[0]?.clientId) {
				debitorQuery = Customer.fetchLazy(this.transports[0]?.clientId)
			}
			if (vessel?.getCompany()?.getApiId()) {
				creditorQuery = Customer.fetchLazy(vessel.getCompany()?.getApiId())
			}
			this.initialDebitor = await debitorQuery
			this.initialCreditor = await creditorQuery

			if (this.salesOrPurchase === InvoiceType.SALES) {
				this.selectedCustomer = this.initialDebitor
			} else {
				this.selectedCustomer = this.initialCreditor
			}
		}

		await this.initialize()

		if (!this.invoice?.getApiId()) {
			this.selectedCustomer = this.invoiceLines[0]?.getCustomer() ?? this.selectedCustomer
		}
	}

	@Watch('invoiceType')
	onInvoiceTypeChanged(newValue) {
		this.salesOrPurchase = newValue || InvoiceType.SALES
	}

	@Watch('invoice.id')
	async triggerInitialize() {
		await this.initialize()
	}

	async initialize() {
		this.$validator.pause()
		this.initializing = true
		this.isEditing = !!this.invoice?.getApiId()

		await this.setStateFromInvoice()
		this.calculateInvoiceTotal()
		try {
			await this.fetchFinancialData()
		} catch (e: any) {
			if (
				e.message.includes('Request failed with status code 498') ||
				(e.response && e.response.status === 498)
			) {
				return this.openExactAuthDialog()
			} else {
				// eslint-disable-next-line
				console.error(e)
				await this.closeCreator()
			}
		}

		if (this.journals.length === 1) {
			this.selectedJournal = this.journals[0]
		}

		if ((!this.invoice && this.isCombinedInvoice) || this.isExternalInvoice) {
			await this.fetchAllFinancialRegistrations()
			this.setSelectedCompanyAndCustomer()
		}

		if (this.invoiceLines.length === 0) {
			this.addInvoiceLine()
		}

		this.invoiceLines.forEach((line) => line.setInvoice(this.internalInvoice))
		if (this.currentCompany?.paymentTermDays) {
			this.internalInvoice.paymentCondition = this.currentCompany.paymentTermDays
		}

		this.initializing = false
		this.initialized = true

		this.$validator.resume()
	}

	setSelectedCompanyAndCustomer() {
		this.companies =
			this.invoiceLines?.map((line) => line.getCustomer()).filter((customer) => customer !== undefined) ?? []
		const allCompany = new Customer()
		allCompany.setApiId('all')
		allCompany.name = this.$t('invoiceCreator.companyType.otherOptions') as string
		allCompany.relationTypes = ['allCustomers']
		this.companies.push(allCompany)
		// default to show all the invoice lines
		this.filterByRelation = allCompany

		this.selectedCustomer =
			this.selectedCustomer ??
			this.customers.find((customer) => customer.getApiId() === this.transports[0].clientId) ??
			null

		if (!this.haveSameClient && !this.haveSameVessel) {
			this.selectedCustomer = null
		}
	}

	async setStateFromInvoice() {
		if (this.invoice) {
			if (this.hasExactFinancialConnection) {
				this.internalInvoice = (await this.invoice.fresh()) as Invoice
			} else {
				this.internalInvoice = this.invoice
			}
			this.selectedCustomer = this.internalInvoice.getCustomer()
			if (this.selectedCustomer) {
				this.customers.push(this.selectedCustomer)
			}
			this.selectedJournal = this.internalInvoice.getJournal()
		} else {
			await this.handleNewInvoice()
		}
		this.invoiceLines = this.internalInvoice.getInvoiceLines()
	}

	async handleNewInvoice() {
		this.internalInvoice = new Invoice()
		if (this.hasInternalFinancialConnection && this.transports?.length === 1) {
			const cargo = await this.transports[0].fetchCargoLazy()
			const invoiceType = this.invoiceType ?? InvoiceType.SALES
			this.internalInvoice = Invoice.createNewFromTransport(null, cargo, invoiceType)
			this.selectedCustomer = this.internalInvoice.getCustomer()
			if (this.selectedCustomer) {
				this.customers.push(this.selectedCustomer)
			}
		}
		this.internalInvoice.isExternal = this.isExternal
	}

	async fetchAllFinancialRegistrations() {
		if (!this.isCombinedInvoice) return

		const transportIds = this.transports?.map((t) => t.getApiId())
		let query = InvoiceLine.query()

		transportIds?.forEach((id) => (query = query.option('filter[transports][]', id as string)))
		query = query.option('filter[financialType]', FinancialType.FINANCIAL_REGISTRATION)

		const financialRegistrations = ((await query.get()) as PluralResponse).getData() as Array<InvoiceLine>

		const notAlreadyInvoiced = financialRegistrations.filter((line) => line.status !== InvoiceLineStatus.INVOICED)

		this.invoiceLines = Invoice.makeInvoiceLinesFromFinancialRegistrations(notAlreadyInvoiced)

		this.lineCounter++
	}

	async fetchFinancialData() {
		await Promise.all([VatCode.fetchAllLazy(this.$store), this.getJournals()])
	}

	private async getJournals() {
		this.journals = await Journal.fetchAllLazy(this.$store)
	}

	openExactAuthDialog() {
		this.$emit('openExactAuthDialog')
	}

	addInvoiceLine() {
		const line = new InvoiceLine()
		line.invoiceType = this.salesOrPurchase
		this.invoiceLines.push(line)
	}

	removeInvoiceLine(toBeRemoved: InvoiceLine) {
		this.invoiceLines = this.invoiceLines.filter((line) => line !== toBeRemoved)
		this.lineCounter++
	}

	calculateInvoiceTotal() {
		this.invoiceTotal = this.filteredInvoiceLines.reduce((acc, line) => {
			return acc + line.total
		}, 0)
	}

	async showPreview() {
		const valid = await this.$validator.validateAll()
		const invoiceLinesValidators = ((this.$refs.invoiceLine || []) as Array<InvoiceLineForm>).map((line) =>
			line.$validator.validateAll()
		)
		const invoiceLinesValids = await Promise.all(invoiceLinesValidators)
		if (!valid || !invoiceLinesValids.every(Boolean)) {
			return
		}
		this.$emit('openPreview')
	}

	buildRequestBody() {
		// add client-side-generated ids to invoiceLines
		let idx = 0
		this.filteredInvoiceLines.forEach((line) => {
			if (!line.getApiId()) {
				line.setApiId('client-side-generated-' + ++idx)
			}
		})

		const invoiceType = this.invoiceType ?? InvoiceType.SALES
		this.internalInvoice.invoiceType = this.isCombinedInvoice ? this.salesOrPurchase : invoiceType

		if (this.setDossierClosed) {
			this.internalInvoice.transportStatus = TransportStatus.CLOSED
		}

		this.internalInvoice.setCustomer(this.selectedCustomer)

		if (this.isCombinedInvoice) {
			const transportIdsFromFilteredInvoiceLines = this.filteredInvoiceLines
				.map((line) => line.getTransport()?.getApiId())
				.reduce((acc: Array<string>, current) => {
					if (current && !acc.includes(current)) {
						acc.push(current)
					}
					return acc
				}, [])
			// @ts-ignore: suppressing type error due to TypeScript's inability to reconcile union types in `.filter`.
			const transportsFromFilteredInvoiceLines = this.transports.filter((t) =>
				transportIdsFromFilteredInvoiceLines.includes(t.getApiId() as string)
			) as Array<FlatTransport> | Array<Transport>

			this.internalInvoice.setTransports(transportsFromFilteredInvoiceLines)
		} else {
			this.internalInvoice.setTransports(this.transports)
		}

		if (this.selectedJournal) {
			this.internalInvoice.setJournal(this.selectedJournal)
		}

		this.internalInvoice.setInvoiceLines(this.filteredInvoiceLines)
		const included: Array<any> = []

		this.filteredInvoiceLines.forEach((line) => {
			const transport = line.getTransport() ?? (this.transports.length === 1 ? this.transports[0] : null)
			line.setTransport(transport as Transport)

			const invoiceType = this.invoiceType ?? InvoiceType.SALES
			line.invoiceType = this.isCombinedInvoice ? this.salesOrPurchase : invoiceType
			// @ts-ignore
			included.push(line.serialize().data)
		})

		// @ts-ignore
		return merge(this.internalInvoice.serialize(), {
			included
		})
	}

	async submit() {
		try {
			this.isLoading = true

			const requestBody = this.buildRequestBody()

			let invoiceId = this.internalInvoice.getApiId()
			if (invoiceId) {
				await axios.patch(`${this.internalInvoice.getModelUrl()}`, requestBody)
			} else {
				const response = await axios.post(
					`${this.internalInvoice.getJsonApiBaseUrl()}${this.internalInvoice.getJsonApiType()}`,
					requestBody
				)
				invoiceId = response.data.data.id
			}

			const invoiceLinesWhichNeedsToBeSynced = this.invoiceLines.filter(
				(invoiceLine) => invoiceLine.syncPrecedingFinancialRegistration
			)
			// sync values from invoice line record to financial registration record
			const registrationsToBeSaved = invoiceLinesWhichNeedsToBeSynced
				.filter((invoiceLine) => !!invoiceLine.getPrecededBy())
				.map((invoiceLine) => {
					const financialRegistration = invoiceLine.getPrecededBy()
					financialRegistration!.quantity = invoiceLine.quantity
					financialRegistration!.unitPrice = invoiceLine.unitPrice
					financialRegistration!.setCustomer(invoiceLine.getCustomer())
					return financialRegistration
				})

			await Promise.all(registrationsToBeSaved.map((financialRegistration) => financialRegistration!.save()))

			if (this.internalInvoice?.isExternal) {
				this.internalInvoice.setApiId(invoiceId)
				this.showAttachments = true
			} else {
				this.$emit('closePreview')
				this.$emit('closed')
				this.$emit('invoiceCreated', this.invoice)
			}
		} catch (e: any) {
			if (e.response && e.response.status === 498) {
				return this.openExactAuthDialog()
			} else {
				throw e
			}
		} finally {
			this.isLoading = false
			await this.$nextTick()
			this.initializing = false
		}
	}

	async finalize() {
		try {
			this.isLoading = true

			await axios.post(
				`${this.internalInvoice.getJsonApiBaseUrl()}${this.internalInvoice.getJsonApiType()}/finalise`,
				{ data: { id: this.internalInvoice.getApiId() } }
			)
		} catch (e: any) {
			if (e.response.status === 498) {
				return this.openExactAuthDialog()
			} else {
				throw e
			}
		} finally {
			this.$emit('closePreview')
			this.$emit('closed')
			this.$emit('invoiceCreated', this.invoice)
			await this.$nextTick()

			this.initializing = false
			this.isLoading = false
		}
	}

	async closeCreator() {
		this.$emit('lineUpdated')
		this.$emit('closed')
		await this.$nextTick()
		this.initializing = false
	}

	async updateInvoiceType(invoiceType: InvoiceType) {
		this.$validator.pause()
		this.salesOrPurchase = invoiceType
		await this.$nextTick()
		this.$validator.resume()
	}

	// A line on a combined invoice belongs to one of its dossiers, and this form has no dossier picker, so
	// combined invoices get no new lines: neither when created nor when an existing one is edited.
	get canAddInvoiceLine() {
		if (this.isCombinedInvoice) return false
		return !(this.isEditing && this.transports.length > 1)
	}

	get isExternalInvoice() {
		return this.internalInvoice?.isExternal ?? false
	}

	get invoiceLinesBySelectedCompany() {
		const isAllCompanies = this.filterByRelation?.getApiId() === 'all'

		return this.invoiceLines?.filter((invoiceLine: InvoiceLine) => {
			return isAllCompanies || invoiceLine.getCustomer()?.getApiId() === this.filterByRelation?.getApiId()
		})
	}

	get filteredInvoiceLines() {
		return (this.isCombinedInvoice ? this.invoiceLinesBySelectedCompany : this.invoiceLines).filter((line) => {
			return line.invoiceType === this.salesOrPurchase
		})
	}

	get hasDifferentCustomers() {
		return (
			new Set(
				this.filteredInvoiceLines
					.filter((line) => line.getCustomer())
					.map((line) => line.getCustomer()!.getApiId())
			).size > 1
		)
	}

	@Watch('filterByRelation')
	updateSelectedCustomer(newValue: Customer) {
		if (!this.isCombinedInvoice) return
		if (newValue.getApiId() === 'all') {
			this.selectedCustomer = null
		} else {
			this.selectedCustomer = newValue
		}
	}

	@Watch('selectedCustomer')
	updatePaymentTermDays() {
		if (this.selectedCustomer?.paymentTermDays) {
			this.internalInvoice.paymentCondition = this.selectedCustomer.paymentTermDays
		}
	}
}
